import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { ALL_COUNCIL_FILTERS, useUserPreferences } from '../hooks/useUserPreferences-v2';
import { EMPTY_PIPELINE_STATS, fetchApps, fetchDADetailRow, fetchPipelineStats, haversine, hasValidCoords } from '../lib/utils';
import { readWithTimeout } from '../lib/supabaseRead';
import { supabase } from '../supabaseClient';
import { useWatchZones } from '../hooks/useWatchZones';
import { useTrackedDAs } from '../hooks/useTrackedDAs';
import { useIsMobile } from '../hooks/useIsMobile';
import Dashboard from './Dashboard';
import NotificationProjects from './NotificationProjects';
import MapView from './MapView';
import MyDAs from './MyDAs';
import SettingsPanel from './SettingsPanel';
import DADetailView from './DADetailView';
import BottomNav from './ui/BottomNav';
import OnboardingOverlay from './OnboardingOverlay';
import CouncilSelector from './CouncilSelector';
import PlanSelector from './PlanSelector';
import { HelpTipsProvider } from '../context/HelpTipsContext';
import { DA_CATEGORIES, DEFAULT_CATEGORIES, normaliseSelectedCategories } from '../lib/daCategories';
import { showToast } from '../lib/toast';
import { createCheckoutSession, PRO_PLANS, STRIPE_CONFIGURED } from '../lib/stripe';
import { flushQueuedEvents, trackAppEvent, trackDAEvent } from '../lib/analytics';
import { BILLING_UNAVAILABLE_MESSAGE, SUBSCRIPTION_LOOKUP_FAILED_MESSAGE } from '../lib/userMessages';
import { preloadTrackedDAProDetails } from '../lib/proDataPreload';
import { filterProAccessibleDAs } from '../lib/proAccess';
import { retryAsync } from '../lib/retry';
import { installClientDiagnostics, reportClientDiagnostic } from '../lib/clientDiagnostics';
import {
  pipelineStatsCacheKey,
  readCachedDashboardApps,
  readCachedPipelineStats,
  refreshAppShell,
  writeCachedDashboardApps,
  writeCachedPipelineStats,
} from '../lib/appRecovery';
import { buildSavedSearchReturnHref, clearSourceReturnHref, writeSourceReturnHref } from '../lib/sourceReturn';
import { filterSearchResultsForIntent, resultMatchesSearchIntent } from '../lib/searchIntent';
import { isSupabaseCircuitError } from '../lib/supabaseCircuitBreaker';
import { getTrackedStatusChanges, mergeTrackedStatusChangeSnapshot } from '../lib/trackedStatusChanges';
import { normaliseCouncilName } from '../config/registry';
import { HIDDEN_STATUSES } from '../lib/constants';

// Full canonical council names — used to normalise DB-stored short names before passing to search_das RPC.
const ALL_COUNCIL_NAMES = ALL_COUNCIL_FILTERS;
const FREE_STANDARD_TRACKED_DA_LIMIT = 1;
const FREE_STANDARD_WATCH_ZONE_LIMIT = 1;
const ACCOUNT_CHOICE_PREFIX = 'ss_account_choice_done:';
const DA_FEED_REVALIDATE_MS = 5 * 60 * 1000;
const SEARCH_MIN_LENGTH = 2;
const SEARCH_PAGE_SIZE = 50;
function searchNamesFromKey(key, fallback = []) {
  return key ? String(key).split('|').filter(Boolean) : fallback;
}

function buildSearchCouncilScope({ isPro, selectedCouncilsKey, subscribedProCouncilsKey }) {
  const selectedCouncilNames = searchNamesFromKey(selectedCouncilsKey, ALL_COUNCIL_NAMES);
  const subscribedProCouncilNames = searchNamesFromKey(subscribedProCouncilsKey);
  if (isPro && subscribedProCouncilNames.length > 0) {
    const normalised = subscribedProCouncilNames.map(normaliseCouncilName).filter(Boolean);
    const intersection = selectedCouncilNames.filter(c => normalised.includes(c));
    return {
      selectedCouncilNames,
      userCouncils: intersection.length > 0 ? intersection : normalised,
    };
  }
  return {
    selectedCouncilNames,
    userCouncils: selectedCouncilNames.length < ALL_COUNCIL_NAMES.length ? selectedCouncilNames : null,
  };
}

function DesktopNavButton({ id, label, active, count, onClick, theme }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 34,
        padding: '7px 12px',
        borderRadius: 8,
        border: `1px solid ${active ? `${theme.accent}55` : 'transparent'}`,
        background: active ? `${theme.accent}22` : 'transparent',
        color: active ? theme.accent : theme.textMuted,
        fontSize: 13,
        fontWeight: 800,
        cursor: 'pointer',
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      <span>{label}</span>
      {count > 0 && id === 'tracked' && (
        <span
          aria-label={`${count} tracked DA update${count === 1 ? '' : 's'}`}
          style={{
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 999,
            background: theme.accent,
            color: theme.textDark,
            fontSize: 10,
            fontWeight: 900,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
}

export default function SiteScopeApp() {
  const { theme, isDark, setIsDark } = useTheme();
  const { session, user } = useAuth();
  const { isPro, tier, loading: subLoading, error: subscriptionError, refresh: refreshTier, proCouncils, proCouncilCount, subscriptionStatus } = useSubscription();
  const userId = user?.id;
  const diagnosticUser = useMemo(() => userId ? { id: userId } : null, [userId]);
  const isPaidAccessTier = ['pro', 'trade', 'business'].includes(tier);
  const hasActivePaidAccess = ['active', 'trialing'].includes(subscriptionStatus);
  const descriptor = tier === 'business'
    ? 'DA Tracking & AI Intelligence'
    : isPro
      ? 'DA Tracking & Intelligence'
      : 'DA Tracking & Alerts';
  const { prefs, savePref, loaded: prefsLoaded } = useUserPreferences(user);
  const isMobile = useIsMobile(900);
  const isDesktop = !isMobile;

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [appsRetrying, setAppsRetrying] = useState(false);
  const [usingCachedApps, setUsingCachedApps] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [showSettings, setShowSettings] = useState(false);
  const [rawSearch, setRawSearch] = useState('');
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = not in search mode
  const [searchMeta, setSearchMeta] = useState(null);
  const [accountChoiceLoaded, setAccountChoiceLoaded] = useState(false);
  const [accountChoiceDismissed, setAccountChoiceDismissed] = useState(false);
  const [standardChoiceLoadRefreshPending, setStandardChoiceLoadRefreshPending] = useState(false);
  const savedScrollY = useRef(0);
  const detailScrollTop = useRef(0);
  const mapReturnRef = useRef(null);
  const lastTrackedSearch = useRef('');
  const dashboardLoadRequestId = useRef(0);
  const appsRef = useRef([]);
  const searchRequestId = useRef(0);
  const withBrowseProposalFields = useCallback((rows) => {
    const browseById = new Map((appsRef.current || []).map(row => [row.id, row]));
    return (rows || []).map(row => {
      const browseRow = browseById.get(row?.id);
      if (!browseRow) return row;
      return {
        ...row,
        // Search RPC omits these browse fields; only reuse the exact record ID.
        application_type: row.application_type || browseRow.application_type,
        council_application_number: row.council_application_number || browseRow.council_application_number || browseRow.da_number,
        proposal_type_ai: row.proposal_type_ai || browseRow.proposal_type_ai,
        proposal_type_ai_confidence: row.proposal_type_ai_confidence ?? browseRow.proposal_type_ai_confidence,
        proposal_type_ai_evidence: row.proposal_type_ai_evidence || browseRow.proposal_type_ai_evidence,
      };
    });
  }, []);

  // Preferences-backed state
  const councils = prefs.selected_councils;
  const selectedCouncils = useMemo(() => Array.isArray(councils) && councils.length > 0 ? councils : ALL_COUNCIL_NAMES, [councils]);
  const selectedCouncilsKey = selectedCouncils.join('|');
  const subscribedProCouncils = useMemo(() => Array.isArray(proCouncils) ? proCouncils : [], [proCouncils]);
  const subscribedProCouncilsKey = subscribedProCouncils.join('|');
  const setCouncils = (v) => {
    const current = Array.isArray(councils) && councils.length > 0 ? councils : ALL_COUNCIL_NAMES;
    const next = typeof v === 'function' ? v(current) : v;
    savePref('selected_councils', Array.isArray(next) && next.length > 0 ? next : ALL_COUNCIL_NAMES);
  };
  const dashPeriod = Number.isFinite(Number(prefs.dashboard_period)) ? Number(prefs.dashboard_period) : 30;
  const setDashPeriod = (v) => savePref('dashboard_period', typeof v === 'string' ? parseInt(v, 10) : v);

  // Sync dark mode from prefs on load
  useEffect(() => {
    if (prefs.dark_mode !== isDark) setIsDark(prefs.dark_mode);
  }, [prefs.dark_mode]);

  useEffect(() => {
    appsRef.current = apps;
  }, [apps]);

  // Sync text size from prefs
  useEffect(() => {
    document.documentElement.setAttribute('data-text-size', prefs.text_size || 'medium');
  }, [prefs.text_size]);

  useEffect(() => {
    if (!user?.id) {
      setAccountChoiceLoaded(true);
      setAccountChoiceDismissed(false);
      return;
    }
    setAccountChoiceLoaded(false);
    try {
      setAccountChoiceDismissed(localStorage.getItem(`${ACCOUNT_CHOICE_PREFIX}${user.id}`) === '1');
    } catch {
      setAccountChoiceDismissed(false);
    } finally {
      setAccountChoiceLoaded(true);
    }
  }, [user?.id]);

  const continueStandardAccount = useCallback(() => {
    if (user?.id) {
      try {
        localStorage.setItem(`${ACCOUNT_CHOICE_PREFIX}${user.id}`, '1');
      } catch {
        /* ignore storage failures */
      }
    }
    setAccountChoiceDismissed(true);
    setStandardChoiceLoadRefreshPending(true);
  }, [user?.id]);

  // Search with 250ms debounce. All signed-in users use the full public DA RPC;
  // Standard users can search beyond the loaded dashboard window, while Pro-only
  // contact/supply-chain fields are still masked by the server.
  // Clears immediately when search is emptied so the normal browse list snaps back.
  useEffect(() => {
    const trimmed = rawSearch.trim();
    if (!trimmed) {
      setSearch('');
      setSearchResults(null);
      setSearchMeta(null);
      setSearchLoading(false);
      setSearchLoadingMore(false);
      return;
    }

    const { selectedCouncilNames, userCouncils } = buildSearchCouncilScope({
      isPro,
      selectedCouncilsKey,
      subscribedProCouncilsKey,
    });

    const getClientSideResults = () => {
      return appsRef.current.filter(a => {
        const councilMatch = selectedCouncilNames.some(c => (a.council || '').toLowerCase().includes(c.toLowerCase()));
        if (!councilMatch) return false;
        return resultMatchesSearchIntent(a, trimmed);
      });
    };

    if (trimmed.length < SEARCH_MIN_LENGTH) {
      searchRequestId.current += 1;
      setSearch(trimmed);
      setSearchResults(() => []);
      setSearchMeta({
        total_count: 0,
        returned_count: 0,
        dashboard_limit: SEARCH_PAGE_SIZE,
        source: 'min_query',
        min_length: SEARCH_MIN_LENGTH,
      });
      setSearchLoading(false);
      setSearchLoadingMore(false);
      return;
    }

    // cancelled is set by the cleanup function when rawSearch changes before this
    // RPC completes. Without this guard, a slow response for "k" can overwrite
    // correct results for "keats" if the user typed quickly and the RPC for "k"
    // returns after the RPC for "keats". clearTimeout only cancels the debounce
    // timer — it has no effect once the async RPC call is already in flight.
    let cancelled = false;
    const requestId = searchRequestId.current + 1;
    searchRequestId.current = requestId;
    setSearch(trimmed);
    setSearchLoading(true);
    setSearchLoadingMore(false);
    setSearchResults(current => current === null ? [] : current);
    setSearchMeta({
      total_count: null,
      returned_count: 0,
      dashboard_limit: SEARCH_PAGE_SIZE,
      source: 'search_das',
      offset: 0,
      page_size: SEARCH_PAGE_SIZE,
      has_more: false,
    });

    const t = setTimeout(async () => {
      try {
        const searchResponse = await readWithTimeout(signal => supabase.rpc('search_das', {
          p_search_text:   trimmed,
          p_user_councils: userCouncils,
          p_limit:         SEARCH_PAGE_SIZE,
          p_offset:        0,
        }).abortSignal(signal), { timeoutMs: 6000, label: 'search_das' });
        const { data, error } = searchResponse;
        if (cancelled || searchRequestId.current !== requestId) return; // stale response — a newer search has already taken over
        if (error) {
          console.error('[search_das] RPC error', error);
          reportClientDiagnostic('search_rpc_failed', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'search',
            page: tab,
            message: error.message || 'Search RPC failed',
            metadata: { search_length: trimmed.length, councils: userCouncils },
            error,
          });
          const fallback = getClientSideResults();
          console.warn('[search_das] using client-side fallback results');
          const filteredFallback = filterSearchResultsForIntent(fallback, trimmed);
          setSearchResults(filteredFallback);
          setSearchMeta({
            total_count: filteredFallback.length,
            returned_count: filteredFallback.length,
            dashboard_limit: filteredFallback.length,
            source: 'client_fallback',
            fallback_reason: 'rpc_error',
          });
        } else {
          const serverResults = withBrowseProposalFields(data ?? []);
          const totalCount = Number(serverResults[0]?.total_count || 0);
          setSearchResults(serverResults);
          setSearchMeta({
            total_count: totalCount,
            returned_count: serverResults.length,
            dashboard_limit: SEARCH_PAGE_SIZE,
            sort_description: 'Results are ranked by relevance, then newest lodged date.',
            source: 'search_das',
            offset: 0,
            page_size: SEARCH_PAGE_SIZE,
            has_more: serverResults.length < totalCount,
          });
        }
      } catch (err) {
        if (cancelled || searchRequestId.current !== requestId) return;
        console.error('[search_das] unexpected error', err);
        reportClientDiagnostic('search_rpc_failed', {
          severity: 'warning',
          user: diagnosticUser,
          surface: 'search',
          page: tab,
          message: err.message || 'Search failed',
          metadata: { search_length: trimmed.length, councils: userCouncils },
          error: err,
        });
        const fallback = getClientSideResults();
        console.warn('[search_das] using client-side fallback results');
        const filteredFallback = filterSearchResultsForIntent(fallback, trimmed);
        setSearchResults(filteredFallback);
        setSearchMeta({
          total_count: filteredFallback.length,
          returned_count: filteredFallback.length,
          dashboard_limit: filteredFallback.length,
          source: 'client_fallback',
          fallback_reason: err?.name === 'AbortError' ? 'timeout' : 'exception',
        });
      } finally {
        if (!cancelled && searchRequestId.current === requestId) setSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [rawSearch, isPro, selectedCouncilsKey, subscribedProCouncilsKey, tab, diagnosticUser, withBrowseProposalFields]);

  const loadMoreSearchResults = useCallback(async () => {
    const trimmed = rawSearch.trim();
    if (
      trimmed.length < SEARCH_MIN_LENGTH ||
      searchLoading ||
      searchLoadingMore ||
      searchMeta?.source !== 'search_das' ||
      !searchMeta?.has_more
    ) {
      return;
    }

    const currentRows = Array.isArray(searchResults) ? searchResults : [];
    const offset = currentRows.length;
    const requestId = searchRequestId.current;
    const { userCouncils } = buildSearchCouncilScope({
      isPro,
      selectedCouncilsKey,
      subscribedProCouncilsKey,
    });
    setSearchLoadingMore(true);

    try {
      const { data, error } = await readWithTimeout(signal => supabase.rpc('search_das', {
        p_search_text:   trimmed,
        p_user_councils: userCouncils,
        p_limit:         SEARCH_PAGE_SIZE,
        p_offset:        offset,
      }).abortSignal(signal), { timeoutMs: 6000, label: 'search_more' });
      if (searchRequestId.current !== requestId) return;
      if (error) throw new Error(error.message || 'Search failed');

      const nextRows = withBrowseProposalFields(data ?? []);
      const byId = new Map(currentRows.map(row => [row.id, row]));
      for (const row of nextRows) if (row?.id) byId.set(row.id, row);
      const merged = [...byId.values()];
      const totalCount = Number(nextRows[0]?.total_count || searchMeta?.total_count || merged.length);
      setSearchResults(merged);
      setSearchMeta(currentMeta => ({
        ...(currentMeta || {}),
        total_count: totalCount,
        returned_count: merged.length,
        dashboard_limit: SEARCH_PAGE_SIZE,
        source: 'search_das',
        offset,
        page_size: SEARCH_PAGE_SIZE,
        has_more: merged.length < totalCount,
      }));
    } catch (err) {
      if (searchRequestId.current !== requestId) return;
      showToast('More search results are temporarily unavailable. Please try again.');
      console.error('[search_das] load more failed', err);
      reportClientDiagnostic('search_load_more_failed', {
        severity: 'warning',
        user: diagnosticUser,
        surface: 'search',
        page: tab,
        message: err.message || 'Search pagination failed',
        metadata: { search_length: trimmed.length, councils: userCouncils, offset },
        error: err,
      });
    } finally {
      if (searchRequestId.current === requestId) setSearchLoadingMore(false);
    }
  }, [
    rawSearch,
    searchLoading,
    searchLoadingMore,
    searchMeta,
    searchResults,
    isPro,
    selectedCouncilsKey,
    subscribedProCouncilsKey,
    diagnosticUser,
    tab,
    withBrowseProposalFields,
  ]);

  const STATUS_GROUPS = {
    'Lodged':           ['Lodged', 'Submitted', 'Open', 'In Progress', 'Pending Lodgement'],
    'Under Assessment': ['Under Assessment'],
    'On Exhibition':    ['On Exhibition'],
    'Approved':         ['Approved', 'Determined', 'Deferred Commencement'],
    'Rejected':         ['Rejected', 'Refused', 'CDC Rejected'],
  };
  const ALL_STATUSES = Object.keys(STATUS_GROUPS);
  const stFilt = useMemo(() => {
    const saved = Array.isArray(prefs.selected_statuses) ? prefs.selected_statuses : ALL_STATUSES;
    const valid = saved.filter(status => ALL_STATUSES.includes(status));
    return valid.length > 0 ? valid : ALL_STATUSES;
  }, [prefs.selected_statuses]);
  const setStFilt = useCallback((value) => {
    const nextValue = typeof value === 'function' ? value(stFilt) : value;
    const valid = Array.isArray(nextValue)
      ? nextValue.filter(status => ALL_STATUSES.includes(status))
      : ALL_STATUSES;
    savePref('selected_statuses', valid.length > 0 ? valid : stFilt);
  }, [savePref, stFilt]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [pipelineStatsLoading, setPipelineStatsLoading] = useState(false);
  const [pipelineStatsRetry, setPipelineStatsRetry] = useState(0);
  const [expId, setExpId] = useState(null);
  const [selectedDA, setSelectedDA] = useState(null);
  const [notificationTarget, setNotificationTarget] = useState(null);
  const [selZone, setSelZone] = useState(null);
  const [mapFocusDA, setMapFocusDA] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(null); // priceId being processed
  const [upgradeBillingMessage, setUpgradeBillingMessage] = useState("");
  const [scrollToExpandedOnArrive, setScrollToExpandedOnArrive] = useState(false);

  // Derive display name from Supabase user metadata
  const rawName = user?.user_metadata?.full_name || user?.user_metadata?.name || '';
  const displayName = rawName.trim() || (user?.email ? user.email.split('@')[0] : '');
  const initials = (() => {
    if (!displayName) return '?';
    if (rawName.trim()) {
      // Full name available — first + last initial
      const parts = rawName.trim().split(/\s+/);
      const first = parts[0][0].toUpperCase();
      const last  = parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : '';
      return first + last;
    }
    // Email fallback — single initial only
    return displayName[0].toUpperCase();
  })();

  const { zones, addZone, addZoneFromMap, updateZoneRadius, deleteZone } = useWatchZones(session, {
    maxZones: isPro ? Infinity : FREE_STANDARD_WATCH_ZONE_LIMIT,
  });
  const { tracked, setTracked, togTrack, isTr } = useTrackedDAs(session, {
    maxTracked: isPro ? Infinity : FREE_STANDARD_TRACKED_DA_LIMIT,
  });
  const proPreloadKey = useRef('');
  const [acknowledgedTrackedChanges, setAcknowledgedTrackedChanges] = useState(() => new Set());
  const [trackedVisitChanges, setTrackedVisitChanges] = useState([]);
  const trackedStatusChanges = useMemo(() => getTrackedStatusChanges(tracked), [tracked]);
  const trackedChangeIds = useMemo(() => (
    trackedStatusChanges
      .filter(da => da.tracked_id)
      .map(da => da.tracked_id)
  ), [trackedStatusChanges]);
  const trackedChangeCount = useMemo(() => (
    trackedChangeIds.filter(id => !acknowledgedTrackedChanges.has(id)).length
  ), [acknowledgedTrackedChanges, trackedChangeIds]);

  useEffect(() => {
    setAcknowledgedTrackedChanges(prev => {
      if (prev.size === 0) return prev;
      const live = new Set(trackedChangeIds);
      const next = new Set([...prev].filter(id => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [trackedChangeIds]);

  useEffect(() => {
    if (tab !== 'tracked') {
      setTrackedVisitChanges([]);
      return;
    }
    setTrackedVisitChanges(prev => mergeTrackedStatusChangeSnapshot(trackedStatusChanges, prev, tracked));
  }, [tab, tracked, trackedStatusChanges]);

  const handleBottomTabChange = useCallback((nextTab) => {
    if (nextTab === 'tracked' && trackedChangeIds.length > 0) {
      setTrackedVisitChanges(prev => mergeTrackedStatusChangeSnapshot(trackedStatusChanges, prev, tracked));
      setAcknowledgedTrackedChanges(prev => {
        const next = new Set(prev);
        trackedChangeIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [tracked, trackedChangeIds, trackedStatusChanges]);

  const handleDesktopTabChange = useCallback((nextTab) => {
    if (mapReturnRef.current?.tab === nextTab && window.history.state?.sitescopeMapOpen) {
      window.history.back();
      return;
    }
    setTab(nextTab);
    handleBottomTabChange(nextTab);
  }, [handleBottomTabChange]);

  useEffect(() => {
    if (!user?.id) return;
    flushQueuedEvents();
    const onOnline = () => flushQueuedEvents();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;
    return installClientDiagnostics(user);
  }, [user?.id, user?.email]);

  useEffect(() => {
    if (!user?.id || !isPro || subLoading || !navigator.onLine || tracked.length === 0) return;

    const preloadCandidates = filterProAccessibleDAs(tracked, { tier, proCouncils: subscribedProCouncils });
    const trackedKey = preloadCandidates.map(da => da.id).filter(Boolean).join('|');
    const preloadKey = `${user.id}:${tier}:${subscribedProCouncils.join(',')}:${trackedKey}`;
    if (!trackedKey || proPreloadKey.current === preloadKey) return;

    proPreloadKey.current = preloadKey;
    const timeoutId = setTimeout(() => {
      preloadTrackedDAProDetails(preloadCandidates, { userId: user.id });
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [user?.id, isPro, subLoading, tracked, tier, subscribedProCouncils]);

  useEffect(() => {
    if (!user) return;
    trackAppEvent('tab_view', { tab }, { page: tab, source: 'bottom_nav' });
  }, [tab, user?.id]);

  // Load apps
  const loadApps = useCallback(({ manual = false, reset = false } = {}) => {
    const requestId = dashboardLoadRequestId.current + 1;
    dashboardLoadRequestId.current = requestId;
    const isCurrentRequest = () => dashboardLoadRequestId.current === requestId;
    const cached = readCachedDashboardApps(userId);
    if (!manual && cached?.apps?.length && (reset || apps.length === 0)) {
      setApps(cached.apps);
      setLastUpdated(cached.cached_at ? new Date(cached.cached_at) : null);
      setUsingCachedApps(true);
      setLoading(false);
    } else if (reset || apps.length === 0) {
      setLoading(true);
    }

    setFetchError(false);
    setAppsRetrying(true);
    retryAsync(fetchApps, {
      retries: 2,
      baseDelayMs: 800,
      shouldRetry: error => !isSupabaseCircuitError(error),
      onRetry: (error, attempt) => {
        console.warn(`[SiteScope] Retrying DA browse load (${attempt})`, error.message || error);
      },
    })
      .then(d => {
        if (!isCurrentRequest()) return;
        setApps(d);
        setLastUpdated(new Date());
        setUsingCachedApps(false);
        writeCachedDashboardApps(userId, d);
      })
      .catch(err => {
        if (!isCurrentRequest()) return;
        console.error('Failed to fetch DAs:', err);
        const fallback = readCachedDashboardApps(userId);
        if (fallback?.apps?.length) {
          setApps(fallback.apps);
          setLastUpdated(fallback.cached_at ? new Date(fallback.cached_at) : null);
          setUsingCachedApps(true);
          setFetchError('cached');
          reportClientDiagnostic('dashboard_using_cached_apps', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'dashboard',
            page: tab,
            message: 'DA browse failed; using cached apps',
            metadata: { cached_at: fallback.cached_at, cached_count: fallback.apps.length },
            error: err,
          });
        } else {
          setFetchError('failed');
          reportClientDiagnostic('dashboard_apps_failed', {
            severity: 'error',
            user: diagnosticUser,
            surface: 'dashboard',
            page: tab,
            message: 'DA browse failed with no cache fallback',
            error: err,
          });
        }
      })
      .finally(() => {
        if (!isCurrentRequest()) return;
        setAppsRetrying(false);
        setLoading(false);
      });
  }, [userId, apps.length, tab, diagnosticUser]);

  useEffect(() => {
    if (!userId) return;
    setApps([]);
    setPipelineStats(null);
    setFetchError(false);
    setUsingCachedApps(false);
    setLastUpdated(null);
    loadApps({ reset: true });
  }, [userId]);

  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (loading || appsRetrying) return;
      const ageMs = lastUpdated ? Date.now() - lastUpdated.getTime() : Infinity;
      if (usingCachedApps || ageMs > DA_FEED_REVALIDATE_MS) {
        loadApps({ manual: true });
      }
    };

    window.addEventListener('pageshow', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', refreshIfStale);
    return () => {
      window.removeEventListener('pageshow', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [appsRetrying, lastUpdated, loadApps, loading, usingCachedApps]);

  useEffect(() => {
    if (!standardChoiceLoadRefreshPending || !accountChoiceDismissed || appsRetrying) return;
    setStandardChoiceLoadRefreshPending(false);
    if (apps.length === 0) loadApps({ manual: true });
  }, [standardChoiceLoadRefreshPending, accountChoiceDismissed, appsRetrying, apps.length, loadApps]);

  // Load pipeline aggregates through the Postgres RPC. This keeps startup from
  // pulling broad DA rows just to calculate a handful of dashboard numbers.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    const councilsForStats = selectedCouncilsKey ? selectedCouncilsKey.split('|') : [];
    const statsKey = pipelineStatsCacheKey(userId, councilsForStats, dashPeriod);
    const cachedStats = readCachedPipelineStats(statsKey);
    setPipelineStatsLoading(true);
    retryAsync(async () => {
      const stats = await fetchPipelineStats(councilsForStats, dashPeriod);
      if (stats?.load_error) throw new Error(stats.load_error);
      return stats;
    }, {
      retries: 2,
      baseDelayMs: 700,
      shouldRetry: error => !isSupabaseCircuitError(error),
      onRetry: (error, attempt) => {
        console.warn(`[SiteScope] Retrying pipeline stats load (${attempt})`, error.message || error);
      },
    })
      .then(stats => {
        if (cancelled) return;
        setPipelineStats(stats);
        writeCachedPipelineStats(statsKey, stats);
      })
      .catch(err => {
        console.error('Failed to fetch pipeline stats:', err);
        if (cancelled) return;
        if (cachedStats?.stats) {
          setPipelineStats({ ...cachedStats.stats, cached_at: cachedStats.cached_at, stale: true });
          reportClientDiagnostic('pipeline_stats_using_cache', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'dashboard',
            page: tab,
            message: 'Pipeline stats failed; using cached stats',
            metadata: { cached_at: cachedStats.cached_at, stats_key: statsKey },
            error: err,
          });
        } else {
          setPipelineStats({
            ...EMPTY_PIPELINE_STATS,
            period_days: dashPeriod,
            load_error: err.message || 'Pipeline stats unavailable',
          });
          reportClientDiagnostic('pipeline_stats_failed', {
            severity: 'error',
            user: diagnosticUser,
            surface: 'dashboard',
            page: tab,
            message: 'Pipeline stats failed with no cache fallback',
            metadata: { stats_key: statsKey },
            error: err,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setPipelineStatsLoading(false);
      });
    return () => { cancelled = true; };
  }, [loading, selectedCouncilsKey, dashPeriod, userId, pipelineStatsRetry, tab, diagnosticUser]);

  // Handle deep-link redirect: when a logged-in user arrives via a share link,
  // DADeepLink stores the target DA in sessionStorage and redirects here.
  useEffect(() => {
    if (loading || !prefsLoaded) return;
    const raw = (() => {
      try { return sessionStorage.getItem('ss_pending_deeplink') || localStorage.getItem('ss_pending_deeplink'); }
      catch { return null; }
    })();
    if (!raw) return;
    let cancelled = false;
    try {
      const link = JSON.parse(raw);
      const type = link.type || 'da';
      const clearPendingDeepLink = () => {
        try { sessionStorage.removeItem('ss_pending_deeplink'); } catch {}
        try { localStorage.removeItem('ss_pending_deeplink'); } catch {}
      };

      if (type === 'da') {
        const found = apps.find(a => link.daId ? a.id === link.daId :
          (a.council_application_number === link.appNumber &&
            (a.council || '').toLowerCase().includes((link.council || '').toLowerCase())));
        const openTarget = da => {
          if (cancelled) return;
          clearPendingDeepLink();
          if (!da) {
            showToast('This application is unavailable. Please try opening the notification again.');
            return;
          }
          setTab('dashboard');
          savedScrollY.current = 0;
          detailScrollTop.current = 0;
          setSelectedDA(da);
          window.history.pushState({ sitescopeDAOpen: true }, '');
          window.scrollTo(0, 0);
        };
        if (found) openTarget(found);
        else if (link.daId) {
          readWithTimeout(() => fetchDADetailRow(link.daId), { label: 'notification_target' })
            .then(({ data }) => openTarget(data));
        } else {
          clearPendingDeepLink();
          showToast('This application could not be found. Please reopen the original link.');
        }
        return () => { cancelled = true; };
      }

      if (type === 'notification') {
        clearPendingDeepLink();
        setTab('dashboard');
        setNotificationTarget(link);
        return;
      }

      if (type === 'tracked') {
        clearPendingDeepLink();
        setTab('tracked');
        return;
      }

      if (type === 'watch_zone') {
        if (zones.length === 0) return;
        clearPendingDeepLink();
        const found = zones.find(z =>
          String(z.id) === String(link.zoneId) ||
          (link.name && z.name.toLowerCase() === link.name.toLowerCase())
        );
        setTab('map');
        if (found) setSelZone(found);
        return;
      }

      if (type === 'saved_search') {
        clearPendingDeepLink();
        if (Array.isArray(link.councils) && link.councils.length > 0) {
          const matched = ALL_COUNCIL_NAMES.filter(c =>
            link.councils.some(value => c.toLowerCase().includes(String(value).toLowerCase()))
          );
          if (matched.length > 0) setCouncils(matched);
        }
        if (Array.isArray(link.statuses) && link.statuses.length > 0) {
          const matched = ALL_STATUSES.filter(status =>
            link.statuses.some(value => status.toLowerCase().includes(String(value).toLowerCase()))
          );
          if (matched.length > 0) setStFilt(matched);
        }
        if (Array.isArray(link.categories) && link.categories.length > 0) {
          const matched = DA_CATEGORIES.map(cat => cat.id).filter(cat =>
            link.categories.some(value => cat.toLowerCase().includes(String(value).toLowerCase()))
          );
          if (matched.length > 0) savePref('selected_categories', matched);
        }
        setTab('dashboard');
        if (link.query) setRawSearch(link.query);
        return;
      }

      if (type === 'patch_summary') {
        clearPendingDeepLink();
        setTab('dashboard');
        setDashPeriod(link.days || 7);
      }
    } catch {}
  }, [loading, prefsLoaded, apps, zones]);

  // Handle Stripe checkout success redirect — only run after user is authenticated
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'success') return;
    // Clean URL first so a refresh doesn't re-trigger this
    window.history.replaceState({}, '', window.location.pathname);
    showToast('Welcome to Pro! Your subscription is now active.');
    // Refresh tier after delay — webhook may take 1-2s to process
    const t1 = setTimeout(() => refreshTier(), 2000);
    const t2 = setTimeout(() => refreshTier(), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [user]);

  // Show onboarding 1.5s after data loads, but only if not already completed
  useEffect(() => {
    if (loading || !prefsLoaded) return;
    const alreadyDone = prefs.onboarding_completed ||
      localStorage.getItem(`ss_onboarding_done:${user?.id || 'guest'}`) === 'true';
    if (alreadyDone) return;
    const t = setTimeout(() => setShowOnboarding(true), 1500);
    return () => clearTimeout(t);
  }, [loading, prefsLoaded, prefs.onboarding_completed, user?.id]);

  // Computed filters and stats
  const now = new Date();
  // isActiveExhibition: date-based — the status column is unreliable for exhibition state
  // (portal reports "Lodged" or "Under Assessment" even when a DA is actively on exhibition)
  const isActiveExhibition = a => {
    const start = a.exhibition_start || a.exhibition_start_date;
    const end   = a.exhibition_end   || a.exhibition_end_date;
    return !!start && !!end && new Date(start) <= now && new Date(end) > now;
  };
  // filt: council + status — used for period stats (unaffected by search).
  // "On Exhibition" filter key uses date range; all other keys use the status column.
  const filt = apps.filter(a => {
    if (HIDDEN_STATUSES.has(a.status)) return false;
    const councilMatch = selectedCouncils.some(c => (a.council || "").toLowerCase().includes(c.toLowerCase()));
    if (!councilMatch) return false;
    if (stFilt.includes('On Exhibition') && (isActiveExhibition(a) || a.status === 'On Exhibition')) return true;
    const otherKeys = stFilt.filter(k => k !== 'On Exhibition');
    const activeDbStatuses = new Set(otherKeys.flatMap(k => STATUS_GROUPS[k] || [k]));
    return activeDbStatuses.has(a.status);
  });
  // isSearchMode: true whenever search results are active (Pro server-side or Standard client-side).
  // filtSearch: search results when in search mode, otherwise council+status-filtered browse list.
  // Search bypasses the dashboard date window; Dashboard still applies visible filters.
  const isSearchMode = searchResults !== null;
  const filtSearch = isSearchMode ? searchResults : filt;
  const periodStart = new Date(now); periodStart.setDate(periodStart.getDate() - dashPeriod);
  const prevStart   = new Date(now); prevStart.setDate(prevStart.getDate() - dashPeriod * 2);
  const inPeriod = a => { if (!a.lodged_date) return false; const dt = new Date(a.lodged_date); return dt >= periodStart && dt <= now; };
  const inPrev   = a => { if (!a.lodged_date) return false; const dt = new Date(a.lodged_date); return dt >= prevStart && dt < periodStart; };
  const filtP    = filt.filter(inPeriod);
  const filtPrev = filt.filter(inPrev);
  const isApp  = a => STATUS_GROUPS['Approved'].includes(a.status);
  // st.exh: snapshot count of DAs currently on exhibition by date (not status column)
  const exhCount = filt.filter(isActiveExhibition).length;
  // Status Overview uses all active council-filtered DAs (not period-filtered)
  const st     = { total: filt.length, exh: exhCount, ass: filt.filter(a => a.status === "Under Assessment").length, app: filt.filter(isApp).length };
  const stPrev = { total: filtPrev.length, exh: exhCount, ass: filtPrev.filter(a => a.status === "Under Assessment").length, app: filtPrev.filter(isApp).length };
  const stTrend = { total: st.total - stPrev.total, exh: 0, ass: st.ass - stPrev.ass, app: st.app - stPrev.app };

  const handleShowOnMap = (da) => {
    if (hasValidCoords(da)) {
      trackDAEvent('da_show_on_map', da, { from_tab: tab }, { page: tab, source: 'show_on_map' });
      if (tab === 'map') {
        setMapFocusDA(da);
        if (selectedDA) handleCloseDA();
        return;
      }
      mapReturnRef.current = { tab, da: selectedDA, scrollY: window.scrollY, feedScrollY: savedScrollY.current, detailScrollTop: detailScrollTop.current };
      window.history.pushState({ sitescopeMapOpen: true }, '');
      setMapFocusDA(da);
      setSelectedDA(null);
      setTab('map');
    }
  };

  const handleOpenDA = (da) => {
    trackDAEvent('da_open', da, {
      from_tab: tab,
      is_search_mode: isSearchMode,
      is_tracked: isTr(da.id),
    }, { page: tab, source: 'da_card' });
    savedScrollY.current = window.scrollY;
    detailScrollTop.current = 0;
    setSelectedDA(da);
    window.scrollTo(0, 0);
    window.history.pushState({ sitescopeDAOpen: true }, '');
    fetchDADetailRow(da.id).then(detail => {
      if (detail) setSelectedDA(current => current?.id === da.id ? { ...current, ...detail } : current);
    });
  };

  const handleTrackDA = (da) => {
    const alreadyTracked = isTr(da.id);
    trackDAEvent(alreadyTracked ? 'da_untrack' : 'da_track', da, {
      from_tab: tab,
      is_search_mode: isSearchMode,
    }, { page: tab, source: 'track_button' });
    return togTrack(da);
  };

  const handleCloseDA = () => {
    if (window.history.state && window.history.state.sitescopeDAOpen) {
      window.history.back(); // popstate handler does the actual close + scroll restore
    } else {
      setSelectedDA(null);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        window.scrollTo(0, savedScrollY.current);
      }));
    }
  };

  // Restore scroll when browser/PWA back gesture closes the DA detail
  useEffect(() => {
    const onPopState = () => {
      if (mapReturnRef.current && !window.history.state?.sitescopeMapOpen) {
        const previous = mapReturnRef.current;
        mapReturnRef.current = null;
        setTab(previous.tab);
        savedScrollY.current = previous.feedScrollY;
        detailScrollTop.current = previous.detailScrollTop || 0;
        setSelectedDA(previous.da);
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, previous.scrollY)));
        return;
      }
      setSelectedDA(prev => {
        if (prev) {
          requestAnimationFrame(() => requestAnimationFrame(() => {
            window.scrollTo(0, savedScrollY.current);
          }));
          return null;
        }
        return prev;
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [tab, tier]);

  const togC = c => setCouncils(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);
  const togSt = s => {
    if (s === "all") { setStFilt(ALL_STATUSES); return; }
    setStFilt(p => {
      const next = p.includes(s) ? p.filter(x => x !== s) : [...p, s];
      return next.length === 0 ? p : next;
    });
  };

  const selectedCategories = normaliseSelectedCategories(prefs.selected_categories).selectedCategories || DEFAULT_CATEGORIES;
  const togCat = (id) => {
    const next = selectedCategories.includes(id) ? selectedCategories.filter(x => x !== id) : [...selectedCategories, id];
    if (next.length > 0) savePref('selected_categories', next);
  };
  const soloCat = (id) => savePref('selected_categories', [id]);
  const filtersCollapsed = prefs.filters_collapsed === true;
  const setFiltersCollapsed = (v) => savePref('filters_collapsed', v);
  const dashboardPipelineCollapsed = prefs.dashboard_pipeline_collapsed === true;
  const setDashboardPipelineCollapsed = (v) => savePref('dashboard_pipeline_collapsed', v);
  const dashboardPipelineOutcomesCollapsed = prefs.dashboard_pipeline_outcomes_collapsed === true;
  const setDashboardPipelineOutcomesCollapsed = (v) => savePref('dashboard_pipeline_outcomes_collapsed', v);
  const dashboardStatusCollapsed = prefs.dashboard_status_collapsed === true;
  const setDashboardStatusCollapsed = (v) => savePref('dashboard_status_collapsed', v);
  const dashboardWatchZonesCollapsed = prefs.dashboard_watch_zones_collapsed === true;
  const setDashboardWatchZonesCollapsed = (v) => savePref('dashboard_watch_zones_collapsed', v);

  // Price range filter — persisted to user_preferences.selected_price_ranges
  // Defensive read: null/undefined/non-array all collapse to empty (no filter)
  const selectedPriceRanges = (() => {
    try { return Array.isArray(prefs.selected_price_ranges) ? prefs.selected_price_ranges : []; }
    catch { return []; }
  })();

  useEffect(() => {
    if (tab !== 'dashboard') return;
    const query = rawSearch.trim();
    if (!query) {
      clearSourceReturnHref();
      return;
    }
    writeSourceReturnHref(buildSavedSearchReturnHref({
      query,
      councils: selectedCouncils,
      statuses: stFilt,
      categories: selectedCategories,
    }));
  }, [tab, rawSearch, selectedCouncilsKey, stFilt, selectedCategories]);

  const togPriceRange = (id) => {
    const next = selectedPriceRanges.includes(id)
      ? selectedPriceRanges.filter(x => x !== id)
      : [...selectedPriceRanges, id];
    savePref('selected_price_ranges', next);
  };
  const clearPriceRanges = () => savePref('selected_price_ranges', []);

  const zApps = z => apps.filter(a => {
    if (!hasValidCoords(a)) return false;
    return haversine(z.lat, z.lng, a.latitude, a.longitude) <= z.radius;
  });

  const handleSelectPlan = useCallback(async (plan) => {
    if (!plan) {
      setUpgradeBillingMessage(BILLING_UNAVAILABLE_MESSAGE);
      showToast(BILLING_UNAVAILABLE_MESSAGE);
      return;
    }
    setUpgradeBillingMessage("");
    trackAppEvent('upgrade_checkout_start', { price_id: plan.priceId || null, plan_key: plan.key, tier }, { page: tab, source: 'upgrade_modal' });
    setUpgradeLoading(plan.key);
    try {
      await createCheckoutSession(plan);
    } catch (err) {
      console.error('[SiteScope] Checkout error:', err.message);
      trackAppEvent('upgrade_checkout_failed', { plan_key: plan.key, error: err.message || 'unknown' }, { page: tab, source: 'upgrade_modal' });
      setUpgradeBillingMessage(BILLING_UNAVAILABLE_MESSAGE);
      showToast(BILLING_UNAVAILABLE_MESSAGE);
      setUpgradeLoading(null);
    }
  }, [tab, tier]);

  useEffect(() => {
    if (!user || !isSearchMode || searchLoading || !search.trim()) return;
    const key = `${search}|${searchResults?.length ?? 0}|${isPro ? 'pro' : 'standard'}`;
    if (lastTrackedSearch.current === key) return;
    lastTrackedSearch.current = key;
    trackAppEvent('search_performed', {
      search_text: search,
      result_count: searchResults?.length ?? 0,
      mode: isPro ? 'pro_rpc' : 'standard_client',
      councils: selectedCouncils,
    }, { page: tab, source: 'search' });
  }, [user?.id, isSearchMode, searchLoading, search, searchResults?.length, isPro, selectedCouncilsKey, tab]);

  const completeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(`ss_onboarding_done:${user?.id || 'guest'}`, 'true');
    savePref('onboarding_completed', true);
  };

  // Gate 1: Subscription still loading — show spinner to prevent dashboard flash.
  if (subLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#081525', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid #30404e', borderTopColor: '#c0a060', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!accountChoiceLoaded) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#081525', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid #30404e', borderTopColor: '#c0a060', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Gate 1b: Paid entitlement lookup failed. Do not send returning paid users
  // to Stripe just because the profile/subscription read had a transient problem.
  // Free Standard users can still use the public-data baseline after signup.
  if (subscriptionError && isPaidAccessTier && !hasActivePaidAccess) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#081525', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ maxWidth: 390, width: '100%', background: '#0d1b2f', border: '1px solid #263653', borderRadius: 10, padding: 24, color: '#e8e0d8', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, marginBottom: 8 }}>Account lookup failed</div>
          <div style={{ fontSize: 14, color: '#9ca3af', lineHeight: 1.5, marginBottom: 18 }}>
            {SUBSCRIPTION_LOOKUP_FAILED_MESSAGE}
          </div>
          <button
            onClick={refreshTier}
            style={{ background: '#c0a060', color: '#081525', border: 'none', borderRadius: 8, padding: '11px 16px', fontWeight: 800, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Gate 2: Paid tier recovery — Standard is the free signed-in baseline.
  // Only paid intelligence tiers are blocked when their entitlement is inactive.
  if (isPaidAccessTier && !hasActivePaidAccess) {
    return <PlanSelector subscriptionStatus={subscriptionStatus} subscriptionError={subscriptionError} />;
  }

  // Gate 2b: First-run account choice. Standard is free, but new users still
  // need to consciously choose Free Standard or a paid Pro account before the app.
  if (tier === 'standard' && !accountChoiceDismissed) {
    return (
      <PlanSelector
        subscriptionStatus={subscriptionStatus}
        subscriptionError={subscriptionError}
        allowFreeStandard
        onContinueStandard={continueStandardAccount}
      />
    );
  }

  // Gate 3: Pro council selection — blocks the app until user selects their councils.
  // Only shown for Pro users who haven't selected yet (count 1-3; All Councils auto-assigns).
  if (tier === 'pro' && proCouncilCount > 0 && proCouncilCount < 4 && (!proCouncils || proCouncils.length === 0)) {
    return (
      <CouncilSelector
        councilCount={proCouncilCount}
        onComplete={() => refreshTier()}
      />
    );
  }

  return (
    <HelpTipsProvider
      showTips={prefs.show_help_tips !== false}
      onAllDismissed={() => savePref('show_help_tips', false)}
    >
    <div style={{
      minHeight: "100vh",
      background: theme.bg,
      color: theme.text,
      fontFamily: "'DM Sans',-apple-system,sans-serif",
      overflowX: "hidden",
      maxWidth: isMobile ? 430 : "none",
      width: "100%",
      margin: "0 auto",
      position: "relative",
    }}>
      {subscriptionError && (
        <div style={{ margin:"0",padding:"9px 14px",background:"rgba(212,168,74,0.12)",borderBottom:`1px solid ${theme.accent}55`,color:theme.accent,fontSize:"var(--text-sm)",fontFamily:"'DM Sans',sans-serif",lineHeight:1.4 }}>
          {SUBSCRIPTION_LOOKUP_FAILED_MESSAGE}
        </div>
      )}
      {showOnboarding && <OnboardingOverlay onComplete={completeOnboarding} />}
      {showSettings && (
        <SettingsPanel
          onClose={() => setShowSettings(false)}
          prefs={prefs}
          savePref={savePref}
          setDashPeriod={setDashPeriod}
          onUpgradeClick={() => setShowUpgrade(true)}
          isDesktopLayout={isDesktop}
        />
      )}

      {/* Upgrade modal */}
      {showUpgrade && !isPro && (
        <div
          onClick={() => setShowUpgrade(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#0a1b36', border: `1.5px solid #c0a060`, borderRadius: '16px', padding: '28px 24px', maxWidth: '440px', width: '100%', fontFamily: "'DM Sans',sans-serif" }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: theme.text, marginBottom: '4px' }}>Upgrade to Pro</div>
                <div style={{ fontSize: '13px', color: theme.textMuted }}>Builder contacts, supply chain intelligence, and more.</div>
              </div>
              <button onClick={() => setShowUpgrade(false)} style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 0 0 12px' }}>✕</button>
            </div>
            {upgradeBillingMessage && (
              <div style={{ marginBottom: '12px', padding: '10px 12px', background: 'rgba(212,168,74,0.1)', border: `1px solid ${theme.accent}55`, borderRadius: 8, color: theme.accent, fontSize: '13px', lineHeight: 1.45 }}>
                {upgradeBillingMessage}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {PRO_PLANS.map(plan => (
                <button
                  key={plan.key}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={!!upgradeLoading}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 16px', borderRadius: '10px', cursor: upgradeLoading ? 'wait' : 'pointer',
                    border: `1.5px solid ${theme.accent}66`, background: upgradeLoading === plan.key ? `${theme.accent}22` : theme.bgCard,
                    color: theme.text, transition: 'border-color 0.15s, background 0.15s',
                    opacity: upgradeLoading && upgradeLoading !== plan.key ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (!upgradeLoading) { e.currentTarget.style.borderColor = theme.accent; e.currentTarget.style.background = `${theme.accent}15`; } }}
                  onMouseLeave={e => { if (upgradeLoading !== plan.key) { e.currentTarget.style.borderColor = `${theme.accent}66`; e.currentTarget.style.background = theme.bgCard; } }}
                >
                  <span style={{ fontWeight: '600', fontSize: '15px' }}>{plan.label}</span>
                  <span style={{ color: theme.accent, fontWeight: '700', fontSize: '15px' }}>
                    {upgradeLoading === plan.key ? 'Loading…' : plan.price}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ marginTop: '16px', fontSize: '12px', color: theme.textMuted, textAlign: 'center' }}>
              {STRIPE_CONFIGURED ? 'All prices include GST. Plans are billed monthly in AUD. Cancel anytime.' : BILLING_UNAVAILABLE_MESSAGE}
            </div>
          </div>
        </div>
      )}

      {notificationTarget && <NotificationProjects hidden={Boolean(selectedDA) || tab !== 'dashboard'}
        target={notificationTarget} onOpenDA={handleOpenDA}
        onClose={() => { setNotificationTarget(null); window.history.replaceState({}, '', '/'); }} theme={theme}
      />}

      {/* DA Detail View — full-screen overlay */}
      {selectedDA && (
        <DADetailView
          key={selectedDA.id}
          app={selectedDA}
          onBack={handleCloseDA}
          initialScrollTop={detailScrollTop.current}
          onScrollPositionChange={value => { detailScrollTop.current = value; }}
          onTrack={handleTrackDA}
          isTr={isTr}
          onShowOnMap={handleShowOnMap}
          theme={theme}
          isDesktopLayout={isDesktop}
        />
      )}

      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${theme.border}`,
        padding: isDesktop ? "14px 24px" : "12px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: theme.bg,
        position: "sticky", top: 0, zIndex: 100,
        gap: "8px",
        boxShadow: isDesktop ? "0 1px 0 rgba(255,255,255,0.02), 0 12px 36px rgba(0,0,0,0.24)" : "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Logo */}
          <div style={{
            width: 32, height: 32, borderRadius: "18.5%",
            background: "linear-gradient(145deg, #c0a060, #8a7040, #a89050)",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "1.5px solid #c0a060",
          }}>
            <span style={{ color: "#0b0f1a", fontSize: 18, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>S</span>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: theme.text, fontSize: 15, fontWeight: 700 }}>SiteScope</span>
              {isPro && (
                <span style={{
                  background: `linear-gradient(145deg, #c0a060, #8a7040, #a89050)`,
                  color: "#f0e8d0", fontSize: 9, fontWeight: 700,
                  padding: "3px 7px", borderRadius: 3, letterSpacing: 1.2,
                  textTransform: "uppercase", border: "1px solid rgba(200,170,100,0.3)",
                }}>PRO</span>
              )}
            </div>
            <div style={{ color: "#6a80a0", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{descriptor}</div>
          </div>
        </div>
        {isDesktop && (
          <nav
            aria-label="Primary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginLeft: "auto",
              marginRight: 8,
            }}
          >
            {[
              { id: 'dashboard', label: 'Dashboard' },
              { id: 'map', label: 'Map' },
              { id: 'tracked', label: 'Tracked' },
            ].map(item => (
              <DesktopNavButton
                key={item.id}
                id={item.id}
                label={item.label}
                active={tab === item.id}
                count={trackedChangeCount}
                onClick={() => handleDesktopTabChange(item.id)}
                theme={theme}
              />
            ))}
          </nav>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Upgrade button — Standard users only */}
          {!isPro && (
            <button
              onClick={() => setShowUpgrade(true)}
              style={{ padding: '6px 12px', borderRadius: '6px', border: `1.5px solid ${theme.accent}`, background: 'transparent', color: theme.accent, fontSize: 'var(--text-xs)', fontWeight: '700', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", letterSpacing: '0.5px', whiteSpace: 'nowrap' }}
              onMouseEnter={e => { e.currentTarget.style.background = `${theme.accent}20`; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >Upgrade</button>
          )}

          {/* User avatar */}
          <button
            data-onboarding="settings-button"
            onClick={() => setShowSettings(true)}
            aria-label="Settings"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: "2px" }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: theme.bgCard, border: `1px solid ${theme.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, color: theme.accent, fontFamily: "'DM Sans',sans-serif",
            }}>
              {initials}
            </div>
          </button>
        </div>
      </div>

      {/* Tab content — padded bottom for nav bar */}
      <div style={{ paddingBottom: isMobile ? 80 : 28 }}>
        {tab === "dashboard" && (
          <Dashboard
            tracked={tracked}
            loading={loading} filt={filtSearch} filtTotal={filt.length} search={search}
            rawSearch={rawSearch} setRawSearch={setRawSearch}
            searchLoading={searchLoading} searchLoadingMore={searchLoadingMore}
            searchMeta={searchMeta} isSearchMode={isSearchMode} isPro={isPro} userId={user?.id}
            onLoadMoreSearch={loadMoreSearchResults}
            st={st} stTrend={stTrend} stPrev={stPrev} period={dashPeriod} zones={zones} theme={theme}
            setPeriod={setDashPeriod}
            setTab={setTab} setSelZone={setSelZone} zApps={zApps}
            councils={selectedCouncils} togC={togC} soloC={c => setCouncils([c])} allC={() => setCouncils(ALL_COUNCIL_NAMES)}
            stFilt={stFilt} setStFilt={togSt} soloStFilt={s => setStFilt([s])} allStFilt={() => setStFilt(ALL_STATUSES)}
            selectedCategories={selectedCategories} togCat={togCat} soloCat={soloCat} allCat={() => savePref('selected_categories', DA_CATEGORIES.map(c => c.id))}
            selectedPriceRanges={selectedPriceRanges} togPriceRange={togPriceRange} clearPriceRanges={clearPriceRanges}
            filtersCollapsed={filtersCollapsed} setFiltersCollapsed={setFiltersCollapsed}
            dashboardPipelineCollapsed={dashboardPipelineCollapsed}
            setDashboardPipelineCollapsed={setDashboardPipelineCollapsed}
            dashboardPipelineOutcomesCollapsed={dashboardPipelineOutcomesCollapsed}
            setDashboardPipelineOutcomesCollapsed={setDashboardPipelineOutcomesCollapsed}
            dashboardStatusCollapsed={dashboardStatusCollapsed}
            setDashboardStatusCollapsed={setDashboardStatusCollapsed}
            dashboardWatchZonesCollapsed={dashboardWatchZonesCollapsed}
            setDashboardWatchZonesCollapsed={setDashboardWatchZonesCollapsed}
            expId={expId} setExpId={setExpId} togTrack={handleTrackDA} isTr={isTr}
            fetchError={fetchError} onRetry={loadApps} lastUpdated={lastUpdated}
            appsRetrying={appsRetrying}
            usingCachedApps={usingCachedApps}
            onRefreshApp={refreshAppShell}
            pipelineStats={pipelineStats}
            pipelineStatsLoading={pipelineStatsLoading}
            onRetryPipelineStats={() => setPipelineStatsRetry(r => r + 1)}
            onShowOnMap={handleShowOnMap}
            onOpenDA={handleOpenDA}
            scrollToExpandedOnArrive={scrollToExpandedOnArrive}
            clearScrollOnArrive={() => setScrollToExpandedOnArrive(false)}
            userEmail={user?.email}
          />
        )}
        {tab === "map" && (
          <MapView
            filt={filtSearch} zones={zones} isDark={isDark} theme={theme} loading={loading}
            togTrack={handleTrackDA} isTr={isTr}
            selZone={selZone} setSelZone={setSelZone}
            addZone={addZone} addZoneFromMap={addZoneFromMap} deleteZone={deleteZone} updateZoneRadius={updateZoneRadius} zApps={zApps}
            mapFocusDA={mapFocusDA} setMapFocusDA={setMapFocusDA} onShowOnMap={handleShowOnMap}
            onViewDADetails={(daId) => {
              // In search mode the map plots search results, which cover the
              // full database — older hits won't be in the browse feed.
              const da = apps.find(a => a.id === daId) || (searchResults || []).find(a => a.id === daId) || (mapReturnRef.current?.da?.id === daId ? mapReturnRef.current.da : null);
              if (da) handleOpenDA(da);
            }}
          />
        )}
        {tab === "tracked" && (
          <MyDAs apps={apps} tracked={tracked} setTracked={setTracked} togTrack={handleTrackDA} theme={theme} onShowOnMap={handleShowOnMap} onOpenDA={handleOpenDA} arrivalStatusChanges={trackedVisitChanges} />
        )}
      </div>

      {/* Bottom Nav — hidden when DA detail is open */}
      {!selectedDA && isMobile && (
        <BottomNav
          tab={tab}
          setTab={handleDesktopTabChange}
          notificationCounts={{ tracked: tab === "tracked" ? 0 : trackedChangeCount }}
        />
      )}
    </div>
    </HelpTipsProvider>
  );
}
